import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Plus, AlertTriangle, Search, Filter, ChevronRight,
  Eye, Edit3, Trash2, MapPin, Wrench, Shield,
  CheckCircle2, Clock, XCircle, MessageSquare
} from 'lucide-react';

const SEVERITY_COLORS = {
  blocker: 'bg-red-100 text-red-800 border-red-300',
  major: 'bg-orange-100 text-orange-800 border-orange-300',
  minor: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  observation: 'bg-slate-100 text-slate-600 border-slate-300',
};

const STATUS_COLORS = {
  open: 'bg-red-100 text-red-700',
  assigned: 'bg-blue-100 text-blue-700',
  acknowledged: 'bg-indigo-100 text-indigo-700',
  in_progress: 'bg-cyan-100 text-cyan-700',
  rectified_by_subcontractor: 'bg-teal-100 text-teal-700',
  awaiting_contractor_check: 'bg-amber-100 text-amber-700',
  awaiting_consultant_reinspection: 'bg-purple-100 text-purple-700',
  closed: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  deferred: 'bg-slate-100 text-slate-600',
  waived: 'bg-slate-100 text-slate-500',
};

export default function CloseoutDefects({ projectId, settings }) {
  const [defects, setDefects] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedDefect, setSelectedDefect] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [packages, setPackages] = useState([]);

  const [form, setForm] = useState({
    description_short: '', description_full: '', package_id: '',
    severity: 'minor', trade_code: '', location_text: '',
    room_name: '', completion_blocker_flag: false,
    assigned_company: '', target_rectification_date: '',
    requires_consultant_reinspection: false,
  });

  const loadDefects = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterSeverity) params.severity = filterSeverity;
      const res = await api.closeout.listDefects(projectId, params);
      setDefects(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [projectId, filterStatus, filterSeverity]);

  useEffect(() => { loadDefects(); }, [loadDefects]);

  useEffect(() => {
    api.closeout.listPackages(projectId).then(res => setPackages(res.data)).catch(() => {});
  }, [projectId]);

  const handleCreate = async () => {
    if (!form.description_short.trim()) { toast.error('Description required'); return; }
    try {
      await api.closeout.createDefect(projectId, form);
      toast.success('Defect created');
      setShowCreate(false);
      setForm({ description_short: '', description_full: '', package_id: '', severity: 'minor', trade_code: '', location_text: '', room_name: '', completion_blocker_flag: false, assigned_company: '', target_rectification_date: '', requires_consultant_reinspection: false });
      loadDefects();
    } catch { toast.error('Failed to create defect'); }
  };

  const loadDefectDetail = async (defectId) => {
    try {
      const res = await api.closeout.getDefect(defectId);
      setSelectedDefect(res.data);
    } catch { toast.error('Failed to load defect'); }
  };

  const updateDefectStatus = async (defectId, newStatus) => {
    try {
      await api.closeout.updateDefect(defectId, { status: newStatus });
      toast.success(`Status updated to ${newStatus.replace(/_/g, ' ')}`);
      loadDefectDetail(defectId);
      loadDefects();
    } catch { toast.error('Update failed'); }
  };

  const filtered = defects.filter(d =>
    !searchTerm || d.description_short?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.defect_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.location_text?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;

  // Detail view
  if (selectedDefect) {
    const d = selectedDefect;
    return (
      <div className="space-y-4" data-testid="defect-detail">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedDefect(null)}>
            <ChevronRight className="h-4 w-4 rotate-180" /> Back
          </Button>
          <div className="flex-1">
            <h2 className="text-lg font-barlow font-bold uppercase flex items-center gap-2">
              <span className="font-mono text-sm text-slate-400">{d.defect_no}</span>
              {d.description_short}
            </h2>
          </div>
          <Badge className={SEVERITY_COLORS[d.severity]}>{d.severity}</Badge>
          <Badge className={STATUS_COLORS[d.status]}>{d.status?.replace(/_/g, ' ')}</Badge>
        </div>

        {d.completion_blocker_flag && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-3 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <span className="text-sm font-medium text-red-700">Completion Blocker</span>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-4 space-y-2">
              <h4 className="text-xs font-bold uppercase text-slate-500">Details</h4>
              {d.location_text && <div className="flex items-center gap-2 text-sm"><MapPin className="h-3.5 w-3.5 text-slate-400" />{d.location_text}</div>}
              {d.room_name && <div className="text-sm text-slate-600">Room: {d.room_name}</div>}
              {d.trade_code && <div className="flex items-center gap-2 text-sm"><Wrench className="h-3.5 w-3.5 text-slate-400" />{d.trade_code.replace(/_/g, ' ')}</div>}
              {d.description_full && <div className="text-sm text-slate-600 mt-2 p-2 bg-slate-50 rounded">{d.description_full}</div>}
              {d.source_type && <div className="text-xs text-slate-400">Source: {d.source_type.replace(/_/g, ' ')}</div>}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 space-y-2">
              <h4 className="text-xs font-bold uppercase text-slate-500">Assignment</h4>
              {d.assigned_company && <div className="text-sm">Company: <strong>{d.assigned_company}</strong></div>}
              {d.assigned_contact && <div className="text-sm">Contact: {d.assigned_contact}</div>}
              {d.target_rectification_date && <div className="text-sm">Due: {new Date(d.target_rectification_date).toLocaleDateString()}</div>}
              {d.requires_consultant_reinspection && <Badge className="mt-1 bg-purple-100 text-purple-700 text-xs">Reinspection Required</Badge>}
            </CardContent>
          </Card>
        </div>

        {/* Status Actions */}
        <Card>
          <CardContent className="p-4">
            <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">Status Actions</h4>
            <div className="flex flex-wrap gap-2">
              {d.status === 'open' && <Button size="sm" variant="outline" onClick={() => updateDefectStatus(d.id, 'assigned')}>Mark Assigned</Button>}
              {d.status === 'assigned' && <Button size="sm" variant="outline" onClick={() => updateDefectStatus(d.id, 'acknowledged')}>Acknowledge</Button>}
              {d.status === 'acknowledged' && <Button size="sm" variant="outline" onClick={() => updateDefectStatus(d.id, 'in_progress')}>Start Work</Button>}
              {d.status === 'in_progress' && <Button size="sm" variant="outline" onClick={() => updateDefectStatus(d.id, 'rectified_by_subcontractor')}>Mark Rectified</Button>}
              {d.status === 'rectified_by_subcontractor' && <Button size="sm" variant="outline" onClick={() => updateDefectStatus(d.id, 'awaiting_contractor_check')}>Send for Contractor Check</Button>}
              {d.status === 'awaiting_contractor_check' && <Button size="sm" variant="outline" onClick={() => updateDefectStatus(d.id, d.requires_consultant_reinspection ? 'awaiting_consultant_reinspection' : 'closed')}>
                {d.requires_consultant_reinspection ? 'Send for Reinspection' : 'Close'}
              </Button>}
              {d.status === 'awaiting_consultant_reinspection' && (
                <>
                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => updateDefectStatus(d.id, 'closed')}>Pass Reinspection</Button>
                  <Button size="sm" variant="destructive" onClick={() => updateDefectStatus(d.id, 'rejected')}>Fail Reinspection</Button>
                </>
              )}
              {d.status === 'rejected' && <Button size="sm" variant="outline" onClick={() => updateDefectStatus(d.id, 'in_progress')}>Reopen for Rework</Button>}
              {!['closed','waived','deferred'].includes(d.status) && (
                <>
                  <Button size="sm" variant="outline" className="text-slate-400" onClick={() => updateDefectStatus(d.id, 'deferred')}>Defer</Button>
                  <Button size="sm" variant="outline" className="text-slate-400" onClick={() => updateDefectStatus(d.id, 'waived')}>Waive</Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Comments */}
        {d.comments?.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">Comments ({d.comments.length})</h4>
              <div className="space-y-2">
                {d.comments.map(c => (
                  <div key={c.id} className="p-2 bg-slate-50 rounded text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium text-slate-700">{c.created_by_name || c.created_by}</span>
                      <span className="text-xs text-slate-400">{new Date(c.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-slate-600 mt-1">{c.comment_text}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-4" data-testid="closeout-defects">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">
          {settings?.profile?.live_defects_enabled ? 'Live Defects / Snags' : 'Snags'}
          <span className="ml-2 text-sm font-normal text-slate-400">({total})</span>
        </h2>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1" onClick={() => setShowCreate(true)} data-testid="create-defect-btn">
          <Plus className="h-4 w-4" /> New Defect
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search defects..."
            className="pl-9"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            data-testid="defect-search"
          />
        </div>
        <Select value={filterStatus} onValueChange={v => setFilterStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="assigned">Assigned</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="rectified_by_subcontractor">Rectified</SelectItem>
            <SelectItem value="awaiting_consultant_reinspection">Awaiting Reinspection</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterSeverity} onValueChange={v => setFilterSeverity(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="All Severity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Severity</SelectItem>
            <SelectItem value="blocker">Blocker</SelectItem>
            <SelectItem value="major">Major</SelectItem>
            <SelectItem value="minor">Minor</SelectItem>
            <SelectItem value="observation">Observation</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Defect List */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Shield className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-lg font-medium text-slate-500">No defects found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1.5">
          {filtered.map(d => (
            <Card key={d.id} className="hover:shadow-sm transition-shadow cursor-pointer" onClick={() => loadDefectDetail(d.id)} data-testid={`defect-${d.id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                {d.completion_blocker_flag && <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />}
                <span className="font-mono text-xs text-slate-400 w-20 flex-shrink-0">{d.defect_no}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-800 truncate">{d.description_short}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {d.location_text && <span className="text-xs text-slate-400">{d.location_text}</span>}
                    {d.trade_code && <Badge variant="outline" className="text-[10px]">{d.trade_code}</Badge>}
                  </div>
                </div>
                <Badge className={`text-[10px] flex-shrink-0 ${SEVERITY_COLORS[d.severity]}`}>{d.severity}</Badge>
                <Badge className={`text-[10px] flex-shrink-0 ${STATUS_COLORS[d.status]}`}>{d.status?.replace(/_/g, ' ')}</Badge>
                <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg">
          <DialogTitle className="font-barlow font-bold uppercase">New Defect / Snag</DialogTitle>
          <DialogDescription>Create a new defect or snag item</DialogDescription>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Description *</Label>
              <Input value={form.description_short} onChange={e => setForm(f => ({...f, description_short: e.target.value}))} placeholder="Brief description of the defect" data-testid="defect-desc-input" />
            </div>
            <div>
              <Label className="text-xs">Full Description</Label>
              <Textarea value={form.description_full} onChange={e => setForm(f => ({...f, description_full: e.target.value}))} rows={2} placeholder="Detailed description..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Package</Label>
                <Select value={form.package_id || "none"} onValueChange={v => setForm(f => ({...f, package_id: v === "none" ? "" : v}))}>
                  <SelectTrigger><SelectValue placeholder="Select package" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No package</SelectItem>
                    {packages.map(p => <SelectItem key={p.id} value={p.id}>{p.package_code} - {p.package_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Severity</Label>
                <Select value={form.severity} onValueChange={v => setForm(f => ({...f, severity: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="blocker">Blocker</SelectItem>
                    <SelectItem value="major">Major</SelectItem>
                    <SelectItem value="minor">Minor</SelectItem>
                    <SelectItem value="observation">Observation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Location</Label>
                <Input value={form.location_text} onChange={e => setForm(f => ({...f, location_text: e.target.value}))} placeholder="e.g. Level 3, Zone A" />
              </div>
              <div>
                <Label className="text-xs">Trade</Label>
                <Input value={form.trade_code} onChange={e => setForm(f => ({...f, trade_code: e.target.value}))} placeholder="e.g. electrical, hvac" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Assigned Company</Label>
                <Input value={form.assigned_company} onChange={e => setForm(f => ({...f, assigned_company: e.target.value}))} placeholder="Subcontractor name" />
              </div>
              <div>
                <Label className="text-xs">Target Date</Label>
                <Input type="date" value={form.target_rectification_date} onChange={e => setForm(f => ({...f, target_rectification_date: e.target.value}))} />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.completion_blocker_flag} onChange={e => setForm(f => ({...f, completion_blocker_flag: e.target.checked}))} className="rounded" />
                Completion Blocker
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.requires_consultant_reinspection} onChange={e => setForm(f => ({...f, requires_consultant_reinspection: e.target.checked}))} className="rounded" />
                Requires Reinspection
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleCreate} data-testid="create-defect-submit">
              <Plus className="h-4 w-4 mr-1" /> Create Defect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
